import { useModal } from '@ebay/nice-modal-react';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import {
  cancelLoginSecretStorageUnlock,
  clearLoginSasVerificationPending,
  completeLoginSecretStorageUnlock,
  isLoginSasVerificationPending,
  setLoginSecretStorageUnlockCancelHandler,
  useLoginSecretStorageUnlock,
} from 'contexts/MatrixContext/loginSecretStorageUnlock';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { MatrixLoggingInStatusEnum } from 'contexts/MatrixContext/utils';
import useMatrixDeviceContext from 'contexts/MatrixDeviceContext/useMatrixDeviceContext';
import useMatrixDeviceVerificationContext from 'contexts/MatrixDeviceVerificationContext/useMatrixDeviceVerificationContext';
import useLogout from 'modules/Authentication/hooks/useLogout';
import useMxCookies from 'modules/Authentication/hooks/useMxCookies';
import ChooseVerificationMethodModal from 'modules/EndToEndEncryption/ChooseVerificationMethodModal/ChooseVerificationMethodModal';
import testingConstants from 'utils/testingConstants';

/**
 * Login-time device verification entry point. Shows the same `ChooseVerificationMethodModal` used
 * post-login ("New device detected") while `startMatrixClient` waits for the user to verify via
 * security code, another device, or reset — instead of jumping straight to the PIN form.
 */
const LoginSecretStorageUnlockGate = () => {
  const navigate = useNavigate();
  const { isRequired } = useLoginSecretStorageUnlock();
  const { clearCookies } = useMxCookies();
  const logout = useLogout();
  const { matrixClientRef, setMatrixLoggingInStatus } = useMatrixContext({ requireDefinedMatrixClient: false });
  const { hasCompletedSelfVerification } = useMatrixDeviceContext();
  const { setHasChosenSasVerification, beginWaitingDeviceFlow } = useMatrixDeviceVerificationContext();
  const { show: showChooseModal, remove: removeChooseModal } = useModal(ChooseVerificationMethodModal);
  const hasShownPickerRef = useRef(false);

  const showChooseModalRef = useRef(showChooseModal);
  const removeChooseModalRef = useRef(removeChooseModal);
  showChooseModalRef.current = showChooseModal;
  removeChooseModalRef.current = removeChooseModal;

  const handleCancel = useCallback(async () => {
    removeChooseModalRef.current();
    cancelLoginSecretStorageUnlock();

    const client = matrixClientRef.current;
    matrixClientRef.current = null;

    if (client) {
      // Full logout — clears crypto/sync IndexedDB, not just cookies (light cancel left IDB behind).
      await logout(client);
    } else {
      clearCookies();
    }

    setMatrixLoggingInStatus(MatrixLoggingInStatusEnum.FINISHED);
    navigate('/auth', { replace: true });
  }, [clearCookies, logout, matrixClientRef, navigate, setMatrixLoggingInStatus]);

  const handleCancelRef = useRef(handleCancel);
  handleCancelRef.current = handleCancel;

  useEffect(() => {
    setLoginSecretStorageUnlockCancelHandler(() => handleCancelRef.current());
    return () => setLoginSecretStorageUnlockCancelHandler(null);
  }, []);

  const beginWaitingDeviceFlowRef = useRef(beginWaitingDeviceFlow);
  beginWaitingDeviceFlowRef.current = beginWaitingDeviceFlow;

  useEffect(() => {
    if (!isRequired) {
      hasShownPickerRef.current = false;
      return;
    }

    if (hasShownPickerRef.current) return;
    hasShownPickerRef.current = true;

    // BUG-007: When the post_bootstrap gate re-opens after a pre_sync SAS bypass, skip the picker
    // and launch the SAS flow directly. The user already clicked "Verify on another device" at the
    // pre_sync gate — re-showing the picker would flash it for a moment before auto-resume fires.
    if (isLoginSasVerificationPending()) {
      clearLoginSasVerificationPending();
      setHasChosenSasVerification(true);
      void beginWaitingDeviceFlowRef.current();
      return;
    }

    showChooseModalRef.current({
      isLoginUnlock: true,
      onLoginUnlockCancel: () => handleCancelRef.current(),
    });
  }, [isRequired, setHasChosenSasVerification]);

  useEffect(() => {
    if (!isRequired || !hasCompletedSelfVerification) return;

    removeChooseModalRef.current();
    completeLoginSecretStorageUnlock();
  }, [hasCompletedSelfVerification, isRequired]);

  useEffect(
    () => () => {
      removeChooseModalRef.current();
    },
    []
  );

  return (
    <div className="h-full w-full" data-testid={testingConstants.loginSecretStorageUnlockGate}>
      <LoadingScreen />
    </div>
  );
};

export default LoginSecretStorageUnlockGate;
